import React from 'react';
import {
  Box, Button, Typography, Tabs, Tab,
} from '@mui/material';
import { formatRut } from 'react-rut-formatter';
import { PayYourLoanProps, PendingInstallmentsInfo } from '../../types/loanPayment';
import AlertDialog from '../../components/AlertDialog';
import PaymentLogin from '../../components/PaymentLogin';
import LoanPayout from './loanPayout';
import TotalPrepay from './totalPrepay';
import ActiveInjectionAlert from './ActiveInjectionAlert';
import MortgagePaymentSection from './MortgagePaymentSection';
import { postPaymentData, getPrepayRequest } from '../../queries/loanPayment';
import analytics from '../../utils/analytics';
import styles from './styles';
import jwtDecode from '../../utils/jwtDecode';
import { Currency, PrepayRequestData, PrepaySimulationData } from '../../types/account';
import { deletePrepayRequest, postSimulatePrepayRequest, getPaymentData } from '../../queries/account';
import LoadingAvatar from '../../components/LoadingAvatar';
import buildDuemintPaymentUrl from '../../utils/buildDuemintPaymentUrl';
import { parseAmountWithCurrency } from '../../utils/helpers';

const EMPTY_PENDING: PendingInstallmentsInfo = {
  amount: 0,
  quantity: 0,
  hasActiveInjections: false,
  hasActiveLoans: false,
};

type PostPaymentResult = {
  loansPending: PendingInstallmentsInfo;
  mortgagePending: PendingInstallmentsInfo;
  loansUrl: string | null;
  mortgageUrl: string | null;
};

export default function PayYourLoan(props: PayYourLoanProps) {
  const {
    token,
    open,
    loanId,
    loanCurrency,
    unpaidAmount,
    paidAmount,
    productType,
    unpaidTotalAmount,
    pageCategory,
    sxButton,
  } = props;

  const [step, setStep] = React.useState<'login' | 'payment'>(open ? 'login' : 'payment');
  const [error, setError] = React.useState<boolean | number>(false);
  const [loansPending, setLoansPending] = React.useState<PendingInstallmentsInfo>(
    EMPTY_PENDING,
  );
  const [mortgagePending, setMortgagePending] = React.useState<PendingInstallmentsInfo>(
    EMPTY_PENDING,
  );
  const [loansCollectionUrl, setLoansCollectionUrl] = React.useState<string | null>(null);
  const [mortgageCollectionUrl, setMortgageCollectionUrl] = React.useState<string | null>(null);
  const [feePercentage, setFeePercentage] = React.useState<{
    feePercentage: number;
    prepayFeePercentage: number;
    feeMaxAmount?: number | null;
  } | null>(null);
  const [loadingPrepay, setLoadingPrepay] = React.useState(true);
  const [simulationData, setSimulationData] = React.useState<PrepaySimulationData>({
    delinquencyDebt: 0,
    interestAmount: 0,
    prepayComission: 0,
    totalToDeposit: 0,
    unpaidCapital: 0,
    currency: { abbreviation: 'CLP', precision: 0 },
    hasPendingCharge: false,
  });
  const [lastPrepayRequestData, setLastPrepayRequestData] = React.useState<
    PrepayRequestData['prepayRequest'] | undefined
  >(undefined);
  const [totalToDeposit, setTotalToDeposit] = React.useState(0);
  const [simulationError, setSimulationError] = React.useState<boolean | number>(false);
  const [prepayIsMoreExpensive, setPrepayIsMoreExpensive] = React.useState(false);
  const [pageName, setPageName] = React.useState('PTC - INGRESO RUT');
  const [openDialog, setOpenDialog] = React.useState(false);
  const [aggregateTab, setAggregateTab] = React.useState(0);

  const getPageName = (
    pInstallments: PendingInstallmentsInfo,
    prepayMoreExpensive: boolean,
    hipotecario = false,
  ) => {
    let name: string;
    if (pInstallments.hasActiveInjections) {
      if (prepayMoreExpensive) {
        name = 'PTC - COBRO EN PROCESO y SIN PREPAGO';
      } else {
        name = 'PTC - COBRO EN PROCESO';
      }
    } else if (pInstallments.quantity === 0) {
      if (prepayMoreExpensive) {
        name = 'PTC - SIN CUOTAS PENDIENTES NI PREPAGO';
      } else {
        name = 'PTC - SIN CUOTAS PENDIENTES';
      }
    } else if (prepayMoreExpensive) {
      name = 'PTC - SIN PREPAGO';
    } else {
      name = 'PTC';
    }
    if (hipotecario) {
      return name.replace(/^PTC/, 'PTC HIPOTECARIO');
    }
    return name;
  };

  const handlePostPaymentData = async (
    idNumber: string,
    captchaToken?: string,
  ): Promise<PostPaymentResult | null> => {
    try {
      let paymentData;
      if (open) {
        const { data } = await postPaymentData({
          idNumber,
          captchaToken: captchaToken || 'any-value',
        });
        paymentData = data;
      } else {
        const { data } = await getPaymentData(token || '');
        paymentData = data;
      }
      if (!paymentData) {
        return null;
      }
      const loansP = paymentData.loansDetails.pendingInstallments;
      const mortgageP = paymentData.mortageDetails.pendingInstallments;
      const loansU = buildDuemintPaymentUrl(
        paymentData.loansDetails.collectionServiceUrl,
        loanCurrency,
      );
      const mortgageU = paymentData.mortageDetails.collectionServiceUrl;
      setLoansPending(loansP);
      setMortgagePending(mortgageP);
      setLoansCollectionUrl(loansU);
      setMortgageCollectionUrl(mortgageU);
      setFeePercentage(paymentData.loansDetails.duemintFeePercentages ?? null);

      const hipotecario = !!(loanId && productType === 'mortgage');
      const primary = hipotecario ? mortgageP : loansP;
      const pageNameTemp = getPageName(primary, false, hipotecario);
      setPageName(pageNameTemp);
      if (step === 'login') {
        setStep('payment');
      }
      return {
        loansPending: loansP,
        mortgagePending: mortgageP,
        loansUrl: loansU,
        mortgageUrl: mortgageU,
      };
    } catch (e) {
      setError(e as number);
    }
    return null;
  };

  const handlePaymentLogin = async (rut: string, recaptchaValue: string | undefined) => {
    if (!rut || !recaptchaValue) {
      return null;
    }
    const result = await handlePostPaymentData(
      formatRut(rut).toLocaleLowerCase(),
      recaptchaValue,
    );
    if (result) {
      const loansHasActive = result.loansPending.hasActiveLoans;
      const mortgageHasActive = result.mortgagePending.hasActiveLoans;
      const bothChannelsActive = !loanId && loansHasActive && mortgageHasActive;
      const anyInjection = result.loansPending.hasActiveInjections
        || result.mortgagePending.hasActiveInjections;
      const allowLoansAuto = !!open && !!result.loansUrl
        && !result.loansPending.hasActiveInjections
        && (loanId ? productType !== 'mortgage' : !bothChannelsActive && loansHasActive);
      const allowMortgageAuto = !!open && !!result.mortgageUrl
        && !result.mortgagePending.hasActiveInjections
        && (loanId ? productType === 'mortgage' : !bothChannelsActive && mortgageHasActive);
      if (anyInjection) {
        analytics.page(pageCategory, 'PTC - COBRO EN PROCESO');
      } else if (allowLoansAuto || allowMortgageAuto) {
        analytics.page(pageCategory, 'PTC - REDIRECCION');
      } else {
        analytics.page(pageCategory, 'PTC');
      }
    }
    setLoadingPrepay(false);
    return null;
  };

  const prepaySimulation = async (): Promise<boolean> => {
    if (!loanId || !token) {
      setSimulationError(false);
      return false;
    }
    try {
      const res = await postSimulatePrepayRequest(token, loanId);
      setSimulationData(res);
      const prepayMoreExpensive = res.totalToDeposit > (unpaidTotalAmount || 0);
      setPrepayIsMoreExpensive(prepayMoreExpensive);
      return prepayMoreExpensive;
    } catch (e) {
      setSimulationError(e as number);
    }
    setLoadingPrepay(false);
    return false;
  };

  const handlePrepayRequest = async () => {
    if (!loanId || !token) {
      return setSimulationError(false);
    }
    const prepayRequests = await getPrepayRequest(token);
    if (prepayRequests.length > 0) {
      const newLastPrepayRequestData = prepayRequests.find(
        (request) => request.prepayRequest?.prepaidLoanId === loanId,
      );
      if (newLastPrepayRequestData && newLastPrepayRequestData.prepayRequest && newLastPrepayRequestData.prepayRequest.type === 'fullPrepay') {
        const totalToDepositTemp = newLastPrepayRequestData.totalToDeposit;
        setLastPrepayRequestData(newLastPrepayRequestData.prepayRequest);
        setTotalToDeposit(totalToDepositTemp);
        return true;
      }
    }
    return false;
  };

  const handlePaymentData = async () => {
    if (!token) {
      return;
    }
    const payload = jwtDecode(token);
    const paymentResult = await handlePostPaymentData(payload.idNumber);
    if (!paymentResult) {
      setLoadingPrepay(false);
      return;
    }
    if (!loanId) {
      setLoadingPrepay(false);
      return;
    }
    if (productType === 'mortgage') {
      setLoadingPrepay(false);
      return;
    }
    const hasPrevPrepayRequest = await handlePrepayRequest();
    if (!hasPrevPrepayRequest) {
      const prepayMoreExpensive = await prepaySimulation();
      const pageNameTemp = getPageName(paymentResult.loansPending, prepayMoreExpensive, false);
      setPageName(pageNameTemp);
    } else {
      setPageName('PTC - PREPAGO PENDIENTE');
    }
    setLoadingPrepay(false);
  };

  const handleArchivePrepayRequest = async () => {
    if (!lastPrepayRequestData?.id || !token) {
      return;
    }
    analytics.track('Click Boton', { text: 'Cancelar solicitud', initiator: 'usuario' });
    setLoadingPrepay(true);
    try {
      await deletePrepayRequest(token, lastPrepayRequestData?.id);
      setLastPrepayRequestData(undefined);
      setTotalToDeposit(0);
      setSimulationError(false);
      await prepaySimulation();
    } catch (e) {
      setSimulationError(e as number);
    } finally {
      setLoadingPrepay(false);
    }
  };

  React.useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError(false);
      }, 5000);
    }
  }, [error]);

  React.useEffect(() => {
    if (step === 'payment' && !open) {
      handlePaymentData();
    }
  }, [step]);

  React.useEffect(() => {
    if (openDialog && !loadingPrepay && !open) {
      analytics.page(pageCategory, pageName);
    }
  }, [openDialog, loadingPrepay]);

  React.useEffect(() => {
    if (open) {
      analytics.page(pageCategory, pageName);
    }
  }, [open]);

  const dataTotalPrepayOkey = (loanId !== undefined
      && unpaidAmount !== undefined
      && paidAmount !== undefined
      && productType !== undefined
      && productType !== 'mortgage'
      && unpaidTotalAmount !== undefined
      && (simulationData.totalToDeposit !== undefined || lastPrepayRequestData !== undefined)
  );

  const isByRut = !!open;
  const loansHasActive = loansPending.hasActiveLoans;
  const mortgageHasActive = mortgagePending.hasActiveLoans;
  const showBothTabs = !loanId && loansHasActive && mortgageHasActive;
  const bothChannelsActive = showBothTabs;

  const allowLoansAutoRedirect = isByRut && !!loansCollectionUrl
    && !loansPending.hasActiveInjections
    && (loanId ? productType !== 'mortgage' : !bothChannelsActive && loansHasActive);
  const allowMortgageAutoRedirect = isByRut && !!mortgageCollectionUrl
    && !mortgagePending.hasActiveInjections
    && (loanId ? productType === 'mortgage' : !bothChannelsActive && mortgageHasActive);

  const isTargetMortgage = !!loanId && productType === 'mortgage';
  const isTargetNonMortgage = !!loanId && productType !== 'mortgage';

  const activeFeePercentage = feePercentage && (
    lastPrepayRequestData === undefined
      ? feePercentage.feePercentage
      : feePercentage.prepayFeePercentage
  );

  const feeMaxAmount = feePercentage?.feeMaxAmount || null;
  const feeLegendCurrency: Currency = loanCurrency ?? { abbreviation: 'CLP', precision: 0 };

  const showFeeLegend = !!activeFeePercentage && (
    (loanId && productType !== 'mortgage')
    || (
      !loanId
      && loansHasActive
      && (!showBothTabs || aggregateTab === 0)
    )
  );

  const renderPaymentContent = () => {
    if (lastPrepayRequestData !== undefined) {
      return null;
    }

    if (isTargetMortgage) {
      return (
        <MortgagePaymentSection
          showActiveInjectionAlert={mortgagePending.hasActiveInjections}
          pendingInstallments={mortgagePending}
          collectionServiceUrl={mortgageCollectionUrl}
          isByRut={isByRut}
          prepayIsMoreExpensive={false}
          allowAutoRedirect={allowMortgageAutoRedirect}
          isAggregateSubpanel={false}
        />
      );
    }

    if (isTargetNonMortgage) {
      return (
        <>
          <ActiveInjectionAlert
            showAlert={loansPending.hasActiveInjections}
          />
          <LoanPayout
            pendingInstallments={loansPending}
            collectionServiceUrl={loansCollectionUrl}
            isByRut={isByRut}
            prepayIsMoreExpensive={prepayIsMoreExpensive}
            showPrepayAdvanceHint
            allowAutoRedirect={allowLoansAutoRedirect}
            isAggregateSubpanel={false}
          />
        </>
      );
    }

    if (showBothTabs) {
      return (
        <>
          <Tabs
            value={aggregateTab}
            onChange={(_, v) => setAggregateTab(v)}
            variant="fullWidth"
            sx={{ marginBottom: 1, marginTop: -10 }}
          >
            <Tab
              label="Otros créditos"
              id="payment-channel-tab-0"
              aria-controls="payment-channel-tabpanel-0"
            />
            <Tab
              label="Crédito hipotecario"
              id="payment-channel-tab-1"
              aria-controls="payment-channel-tabpanel-1"
            />
          </Tabs>
          <div
            role="tabpanel"
            hidden={aggregateTab !== 0}
            id="payment-channel-tabpanel-0"
            aria-labelledby="payment-channel-tab-0"
            style={{ width: '100%' }}
          >
            {aggregateTab === 0 && (
              <>
                <ActiveInjectionAlert showAlert={loansPending.hasActiveInjections} />
                <LoanPayout
                  pendingInstallments={loansPending}
                  collectionServiceUrl={loansCollectionUrl}
                  isByRut={isByRut}
                  prepayIsMoreExpensive={prepayIsMoreExpensive}
                  showPrepayAdvanceHint
                  allowAutoRedirect={false}
                  isAggregateSubpanel
                />
              </>
            )}
          </div>
          <div
            role="tabpanel"
            hidden={aggregateTab !== 1}
            id="payment-channel-tabpanel-1"
            aria-labelledby="payment-channel-tab-1"
            style={{ width: '100%' }}
          >
            {aggregateTab === 1 && (
              <MortgagePaymentSection
                showActiveInjectionAlert={mortgagePending.hasActiveInjections}
                pendingInstallments={mortgagePending}
                collectionServiceUrl={mortgageCollectionUrl}
                isByRut={isByRut}
                prepayIsMoreExpensive={false}
                allowAutoRedirect={false}
                isAggregateSubpanel
              />
            )}
          </div>
        </>
      );
    }

    if (loansHasActive) {
      return (
        <>
          <ActiveInjectionAlert showAlert={loansPending.hasActiveInjections} />
          <LoanPayout
            pendingInstallments={loansPending}
            collectionServiceUrl={loansCollectionUrl}
            isByRut={isByRut}
            prepayIsMoreExpensive={prepayIsMoreExpensive}
            showPrepayAdvanceHint
            allowAutoRedirect={allowLoansAutoRedirect}
            isAggregateSubpanel={false}
          />
        </>
      );
    }

    if (mortgageHasActive) {
      return (
        <MortgagePaymentSection
          showActiveInjectionAlert={mortgagePending.hasActiveInjections}
          pendingInstallments={mortgagePending}
          collectionServiceUrl={mortgageCollectionUrl}
          isByRut={isByRut}
          prepayIsMoreExpensive={false}
          allowAutoRedirect={allowMortgageAutoRedirect}
          isAggregateSubpanel={false}
        />
      );
    }

    return (
      <>
        <ActiveInjectionAlert showAlert={loansPending.hasActiveInjections} />
        <LoanPayout
          pendingInstallments={loansPending}
          collectionServiceUrl={loansCollectionUrl}
          isByRut={isByRut}
          prepayIsMoreExpensive={prepayIsMoreExpensive}
          showPrepayAdvanceHint
          allowAutoRedirect={allowLoansAutoRedirect}
          isAggregateSubpanel={false}
        />
      </>
    );
  };

  return (
    <AlertDialog
      isTriggered={open}
      button={open ? undefined : <Button sx={sxButton} variant="contained" color="primary" onClick={() => setOpenDialog(true)}>Paga tu crédito</Button>}
      hideSubmitButton
      hideCloseButton={open}
      onCloseAction={() => setOpenDialog(false)}
      maxWidth="xs"
      legend={showFeeLegend && feePercentage && (
        <Typography
          variant="caption"
          color="primary"
          sx={{
            paddingInline: 2,
            fontSize: '10.5px',
            fontStyle: 'italic',
          }}
        >
          El pago por este medio tiene un cargo adicional de
          {' '}
          <span>{`${activeFeePercentage * 100}%`}</span>
          {feeMaxAmount && (
            <span>
              {', con un máximo de '}
              {parseAmountWithCurrency(feeLegendCurrency, feeMaxAmount)}
              {' por cuota'}
            </span>
          )}
          <span>.</span>
        </Typography>
      )}
      dialogContent={
          step === 'login' ? (
            <PaymentLogin
              handlePaymentLogin={handlePaymentLogin}
              errorEndpoint={error || false}
            />
          ) : (
            <Box sx={styles.boxContainer}>
              {(lastPrepayRequestData === undefined && !loadingPrepay) && renderPaymentContent()}
              {loadingPrepay && !open && (
              <LoadingAvatar loading={loadingPrepay} />
              )}
              {(!open && dataTotalPrepayOkey && !loadingPrepay) && (
              <TotalPrepay
                loanId={loanId}
                unpaidAmount={unpaidAmount}
                paidAmount={paidAmount}
                productType={productType}
                unpaidTotalAmount={unpaidTotalAmount}
                simulationData={simulationData}
                simulationError={simulationError}
                lastPrepayRequestData={lastPrepayRequestData}
                totalToDeposit={totalToDeposit}
                collectionServiceUrl={loansCollectionUrl}
                handleArchivePrepayRequest={handleArchivePrepayRequest}
                hasActiveInjections={loansPending.hasActiveInjections}
                pageCategory={pageCategory}
              />
              )}
            </Box>
          )
      }
    />
  );
}

PayYourLoan.defaultProps = {
  open: false,
};
